/**
 * News pins for the odds chart: headlines about the two teams, placed at the time they ran, so a
 * reader can see what was in the news when the odds moved. Facts only: a pin says WHEN something
 * was reported, never that it moved the price.
 *
 * MOCKUP SOURCE: ESPN's public site API (free and unofficial — fine for a mockup, read through the
 * dev server's proxy). Production should read news through our own server, and may add a licensed
 * injury feed.
 */
import type { SideSeries } from "./history";

export interface NewsItem {
  id: string;
  /** Unix ms, when it was published. */
  t: number;
  headline: string;
  source: string;
  url: string | null;
}

/** Our league name → ESPN's sport path. */
const ESPN_SPORT: Record<string, string> = {
  NFL: "football/nfl",
  NCAAF: "football/college-football",
  MLB: "baseball/mlb",
  NBA: "basketball/nba",
  WNBA: "basketball/wnba",
  NHL: "hockey/nhl",
};

export function espnSport(league: string | undefined): string | null {
  return league ? (ESPN_SPORT[league.toUpperCase()] ?? null) : null;
}

interface EspnTeam {
  team?: { id?: string; displayName?: string; shortDisplayName?: string; name?: string };
}

/** Find a team's ESPN id by name, from `GET …/{sport}/teams`. */
export function espnTeamId(teamsJson: unknown, name: string): string | null {
  const leagues = (teamsJson as { sports?: { leagues?: { teams?: EspnTeam[] }[] }[] })?.sports?.[0]?.leagues ?? [];
  const want = name.trim().toLowerCase();
  for (const league of leagues) {
    for (const { team } of league.teams ?? []) {
      const names = [team?.displayName, team?.shortDisplayName, team?.name].map((n) => n?.toLowerCase());
      if (team?.id && names.includes(want)) return team.id;
    }
  }
  return null;
}

interface EspnArticle {
  id?: number | string;
  type?: string;
  headline?: string;
  published?: string;
  links?: { web?: { href?: string } };
}

/**
 * Headlines from ESPN's news JSON. Only "HeadlineNews" — ESPN's breaking-news type — counts; the
 * feed also carries features, betting previews and video clips, which aren't news events.
 */
export function parseEspnNews(json: unknown): NewsItem[] {
  const articles = (json as { articles?: EspnArticle[] })?.articles ?? [];
  const out: NewsItem[] = [];
  for (const a of articles) {
    const t = a.published ? Date.parse(a.published) : NaN;
    if (a.type !== "HeadlineNews" || !a.headline || !Number.isFinite(t)) continue;
    out.push({ id: `espn-${a.id ?? t}`, t, headline: a.headline, source: "ESPN", url: a.links?.web?.href ?? null });
  }
  return out;
}

/** Merge several teams' feeds: one copy of each story, newest last. */
export function mergeNews(lists: NewsItem[][]): NewsItem[] {
  const byId = new Map<string, NewsItem>();
  for (const list of lists) for (const n of list) byId.set(n.id, n);
  return [...byId.values()].sort((a, b) => a.t - b.t);
}

export interface NewsPin extends NewsItem {
  /** 1-based, in time order — the number on the chart and in the list. */
  n: number;
}

/** The headlines that fall inside the chart, numbered, at most `max` (the latest ones). */
export function chartPins(news: NewsItem[], t0: number, t1: number, max = 5): NewsPin[] {
  return news
    .filter((n) => n.t >= t0 && n.t <= t1)
    .slice(-max)
    .map((n, i) => ({ ...n, n: i + 1 }));
}

/**
 * The pin closest in time to the biggest single step on any line — the one worth putting on a share
 * card. Null when there are no pins.
 */
export function keyPin(pins: NewsPin[], series: SideSeries[]): NewsPin | null {
  if (!pins.length) return null;
  let stepT: number | null = null;
  let stepSize = 0;
  for (const s of series) {
    for (let i = 1; i < s.points.length; i++) {
      const d = Math.abs(s.points[i].price - s.points[i - 1].price);
      if (d > stepSize) {
        stepSize = d;
        stepT = s.points[i].t;
      }
    }
  }
  if (stepT === null) return pins[pins.length - 1];
  const at = stepT;
  return pins.reduce((best, p) => (Math.abs(p.t - at) < Math.abs(best.t - at) ? p : best));
}
